import { CalculationRowType, UserDefinedRow } from "../model";
import { DatelyObject } from "../util";
import { RowUsecaseCalcData } from "./RowUsecaseCalcData";
import { UsecaseValidationMessage } from "./UsecaseValidationMessage";

/**
 * Client-side logic for Usecase.
 */
export abstract class BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param _row Row to update.
   * @param _date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   * @param _calcData The calculation related data that is required by the more complex calculations:
   * This data is available only after first round-trip to the server (results object exists) and not all user interfaces may provide this.
   */
  public updateUsecase(_row: UserDefinedRow, _date: DatelyObject, _calcData: RowUsecaseCalcData): void {
    // Do nothing by default.
  }

  /**
   * Gets validation message for the usecase.
   * @param row Row to validate
   * @returns A message that is either an error or OK (default). Warnings, info etc. may later be supported.
   */
  public getValidation(row: UserDefinedRow): UsecaseValidationMessage {
    if (row.price < 0) {
      if (!this.canHaveNegativeRow(row.rowType)) {
        return {
          type: "error",
          msg: "SALAXY.VALIDATION.Usecases.negativeRow",
        };
      }
    }
    if (row.count < 0) {
      return {
        type: "error",
        msg: "SALAXY.VALIDATION.Usecases.negativeAmount",
      };
    }
    if (this.getUcValidation) {
      return this.getUcValidation(row);
    }
    return null;
  }

  /** Internal validation method that deriving classes should implement. */
  protected abstract getUcValidation?(row: UserDefinedRow): UsecaseValidationMessage;

  /**
   * Returns true if the row type supports negative values.
   * Typically, this means that the sum of all rows must still be zero or positive.
   * @param rowType Row type to check
   */
  private canHaveNegativeRow(rowType: CalculationRowType): boolean {
    // NOTE: This is also validated server-side (GetSupportsNegative()), so you need to add it there as well.
    const supportsNegative: CalculationRowType[] = [
      CalculationRowType.MonthlySalary,
      CalculationRowType.Salary,
      CalculationRowType.Compensation,
      CalculationRowType.Expenses,
    ];
    return !!supportsNegative.find((x) => x === rowType);
  }
}